import { useEffect, useMemo, useRef, useState } from 'react'
import { LOOKS, lookIds, DEFAULT_LOOK } from '../lib/proposalLooks'
import RecipientPicker from './RecipientPicker'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { toast } from './Toast'
import { isOpen as isOpenLead } from '../lib/leadPipeline'
import {
  KINDS, startsFor, defaultStart, recipientKey, parseRecipient, quoteDraft, bidDraft, destination, chooserBlocker,
} from '../lib/newProposal'
import { PRESETS, loadCatalog, presetForLead } from '../lib/proposalPresets'
import PackageCards from '../pages/proposals/PackageCards'

/**
 * "New proposal": a quote for a client or lead, or a bid on a public RFP.
 *
 * The two start in different records and open in different places — a quote
 * in the proposal studio, a bid in its workspace, where the requirements are —
 * so the choice is made here, once, before anything is created.
 *
 * `initialClientId` preselects the client (a "New proposal" opened from a
 * client's page); the select can still change it. `initialLeadId` does the
 * same for a lead and also starts on the package their request points to.
 * `onCreated(row)` runs once the proposal exists, before the studio opens.
 */

const genToken = () => {
  const bytes = new Uint8Array(16)
  const c = typeof window !== 'undefined' ? window.crypto : null
  if (c?.getRandomValues) c.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
}

const legend = { fontFamily: 'var(--fu)', fontSize: 13, fontWeight: 700, color: 'var(--txt)', marginBottom: 8, padding: 0 }
const control = {
  height: 42, boxSizing: 'border-box', width: '100%', border: '1px solid var(--bdr2)', borderRadius: 8,
  background: 'var(--card)', color: 'var(--txt)', fontFamily: 'var(--fu)', fontSize: 14, padding: '0 10px',
}

function KindCard({ kind, checked, onPick }) {
  return (
    <label style={{
      display: 'flex', flexDirection: 'column', gap: 8, padding: 16, cursor: 'pointer', borderRadius: 12,
      border: checked ? '2px solid var(--navy)' : '1px solid var(--bdr2)',
      margin: checked ? 0 : 1, background: 'var(--card)',
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="radio" name="np-kind" value={kind.id} checked={checked} onChange={() => onPick(kind.id)}
          style={{ margin: 0, accentColor: 'var(--accent)' }} />
        <span style={{ fontFamily: 'var(--fu)', fontSize: 15, fontWeight: 800, color: 'var(--navy)' }}>{kind.label}</span>
      </span>
      <span style={{ fontFamily: 'var(--fu)', fontSize: 13, lineHeight: 1.5, color: 'var(--mut)' }}>{kind.blurb}</span>
    </label>
  )
}

function Pill({ id, label, checked, onPick }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 12px', borderRadius: 18, cursor: 'pointer',
      border: checked ? '2px solid var(--navy)' : '1px solid var(--bdr2)', margin: checked ? 0 : 1,
      fontFamily: 'var(--fu)', fontSize: 13, fontWeight: checked ? 700 : 500, color: 'var(--txt)',
    }}>
      <input type="radio" name="np-from" value={id} checked={checked} onChange={() => onPick(id)}
        style={{ margin: 0, accentColor: 'var(--accent)' }} />
      {label}
    </label>
  )
}

// A thumbnail of each look's cover, so the choice is visual.
function LookSwatch({ id }) {
  const box = { width: 40, height: 52, flexShrink: 0, position: 'relative', overflow: 'hidden', border: '1px solid var(--bdr)' }
  if (id === 'swiss') return <span aria-hidden="true" style={{ ...box, background: '#1A1A1A' }}><span style={{ position: 'absolute', top: 0, left: 0, width: 14, height: 14, background: '#EF4237' }} /><span style={{ position: 'absolute', left: 6, bottom: 12, width: 24, height: 3, background: '#FFFFFF' }} /></span>
  if (id === 'bold') return <span aria-hidden="true" style={{ ...box, background: '#EF4237' }}><span style={{ position: 'absolute', left: 6, top: 16, width: 26, height: 3, background: '#FFFFFF' }} /><span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 13, background: '#1A1A1A' }} /></span>
  return <span aria-hidden="true" style={{ ...box, background: '#FFFFFF' }}><span style={{ position: 'absolute', left: 6, top: 16, width: 10, height: 3, background: '#EF4237' }} /><span style={{ position: 'absolute', left: 6, top: 23, width: 26, height: 3, background: '#1A1A1A' }} /><span style={{ position: 'absolute', left: 6, top: 30, width: 20, height: 3, background: '#1A1A1A' }} /></span>
}

export default function NewProposalDialog({ open, onClose, initialKind = 'quote', initialClientId = null, initialLeadId = null, onCreated }) {
  const navigate = useNavigate()
  const first = useRef(null)
  const [kind, setKind] = useState(initialKind)
  const [start, setStart] = useState(defaultStart(initialKind))
  const [who, setWho] = useState('')
  const [organization, setOrganization] = useState('')
  const [title, setTitle] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [look, setLook] = useState(DEFAULT_LOOK)
  const [pastId, setPastId] = useState('')
  const [presetId, setPresetId] = useState('')
  const [data, setData] = useState({ clients: [], leads: [], templates: [], proposals: [], bids: [], loaded: false })
  // The live price list: what the package cards are priced from and what a
  // picked package is built with — the same read as the studio's.
  const [catalog, setCatalog] = useState({ services: [], loaded: false })
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return undefined
    setKind(initialKind); setStart(defaultStart(initialKind)); setOrganization(''); setTitle(''); setTemplateId(''); setPastId(''); setPresetId(''); setLook(DEFAULT_LOOK)
    setWho(initialClientId ? recipientKey('client', initialClientId) : initialLeadId ? recipientKey('lead', initialLeadId) : '')
    let alive = true
    loadCatalog(supabase).then(c => { if (alive) setCatalog({ ...c, loaded: true }) })
    ;(async () => {
      const safe = async (q) => { try { const r = await q; return Array.isArray(r?.data) ? r.data : [] } catch { return [] } }
      const [clients, leads, templates, proposals, bids] = await Promise.all([
        safe(supabase.from('clients').select('id, name, company, email').order('name')),
        safe(supabase.from('leads').select('id, name, company, email, status, budget, project_type, description, timeline').order('created_at', { ascending: false })),
        safe(supabase.from('proposal_templates').select('*').eq('is_active', true).order('name')),
        safe(supabase.from('proposals').select('id, title, total, content, validity_days, proposal_type, created_at').eq('proposal_type', 'rich').order('created_at', { ascending: false }).limit(30)),
        safe(supabase.from('bids').select('id, title, organization, intro, scope_items, pricing, created_at').order('created_at', { ascending: false }).limit(30)),
      ])
      if (!alive) return
      setData({ clients, leads: leads.filter(isOpenLead), templates, proposals, bids, loaded: true })
      // A lead brings what they asked for: the package it points to starts chosen.
      const lead = initialLeadId ? leads.find(l => String(l.id) === String(initialLeadId)) : null
      const suggested = presetForLead(lead)
      if (suggested) setPresetId(prev => prev || suggested)
    })()
    setTimeout(() => first.current?.focus(), 30)
    return () => { alive = false }
  }, [open, initialKind, initialClientId, initialLeadId])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const templates = useMemo(() => data.templates.filter(t => (t.kind || 'proposal') === (kind === 'bid' ? 'bid' : 'proposal')), [data.templates, kind])
  const pasts = kind === 'bid' ? data.bids : data.proposals.filter(p => (p.content?.sections || []).length > 0)
  const recipient = parseRecipient(who)
  const blocker = chooserBlocker({ kind, recipient, organization, start, templateId, pastId, presetId })

  if (!open) return null

  async function go() {
    if (blocker || busy) return
    setBusy(true)
    try {
      const designed = templateId.startsWith('look:')
      const template = designed ? null : (templates.find(t => t.id === templateId) || null)
      const past = pasts.find(p => p.id === pastId) || null
      if (kind === 'bid') {
        const row = bidDraft({ title, organization, start, template, past, token: genToken() })
        const { data: created, error } = await supabase.from('bids').insert(row).select('id').single()
        if (error) throw error
        onClose?.()
        navigate(destination('bid', created.id))
        return
      }
      const record = recipient
        ? (recipient.type === 'client' ? data.clients : data.leads).find(r => String(r.id) === recipient.id) || null
        : null
      const preset = PRESETS.find(p => p.id === presetId) || null
      const { count } = await supabase.from('proposals').select('*', { count: 'exact', head: true })
      const row = quoteDraft({
        recipient, record, start: designed ? 'blank' : start, template, past, preset, catalog,
        proposalNumber: `P-${String((count || 0) + 1).padStart(4, '0')}`,
        token: genToken(),
      })
      // The look (Editorial, Swiss grid, Bold red) travels with the content.
      row.content = { ...(row.content || {}), look }
      const { data: created, error } = await supabase.from('proposals').insert(row).select().single()
      if (error) throw error
      // The caller's follow-up (a lead moving to Qualified) is secondary: the
      // proposal exists either way, so a failure there never blocks opening it.
      try { await onCreated?.(created) } catch { /* reported by the caller */ }
      onClose?.()
      navigate(destination('quote', created.id))
      const gone = start === 'package' && preset ? preset.services.filter(id => !catalog.services.some(sv => sv.id === id)) : []
      if (gone.length) toast.error(`${gone.join(', ')} ${gone.length === 1 ? 'is' : 'are'} no longer on the price list and ${gone.length === 1 ? 'was' : 'were'} left out.`)
    } catch (e) {
      toast.error(`Could not create it: ${e?.message || 'unknown error'}`)
    } finally {
      setBusy(false)
    }
  }

  const startOptions = startsFor(kind)

  // Portalled to the body so no ancestor's stacking or overflow can clip it.
  return createPortal(
    <>
      <div onClick={onClose} aria-hidden="true" style={{ position: 'fixed', inset: 0, background: 'rgba(20,20,19,0.45)', zIndex: 400 }} />
      <div role="dialog" aria-modal="true" aria-labelledby="np-title" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 401,
        width: 640, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', boxSizing: 'border-box',
        background: 'var(--card)', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: 24,
        display: 'flex', flexDirection: 'column', gap: 18, fontFamily: 'var(--fu)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 id="np-title" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>New proposal</h2>
          <button type="button" aria-label="Close" onClick={onClose}
            style={{ width: 36, height: 36, border: 'none', borderRadius: 8, background: 'transparent', color: 'var(--mut)', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        <fieldset style={{ margin: 0, padding: 0, border: 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <legend style={legend}>What are you sending?</legend>
          {KINDS.map(k => <KindCard key={k.id} kind={k} checked={kind === k.id} onPick={(id) => {
            setKind(id); setPastId(''); setTemplateId('')
            // A bid has no package start; leaving it selected would hide every option.
            if (id === 'bid' && start === 'package') setStart('blank')
          }} />)}
        </fieldset>

        {kind === 'quote' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ ...legend, marginBottom: 0 }}>For</span>
            <RecipientPicker ref={first} clients={data.clients} leads={data.leads} value={who} onChange={setWho} keyFor={recipientKey} />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <label style={{ ...legend, display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 0 }}>Agency
              <input ref={first} value={organization} onChange={e => setOrganization(e.target.value)} placeholder="City of …" style={{ ...control, fontWeight: 400 }} />
            </label>
            <label style={{ ...legend, display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 0 }}>Solicitation title
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Brand refresh and website" style={{ ...control, fontWeight: 400 }} />
            </label>
          </div>
        )}

        <fieldset style={{ margin: 0, padding: 0, border: 'none', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <legend style={legend}>Start from</legend>
          {startOptions.map(s => <Pill key={s.id} id={s.id} label={s.label} checked={start === s.id} onPick={setStart} />)}
        </fieldset>

        {start === 'package' && kind === 'quote' && (
          catalog.services.length ? (
            <>
              <PackageCards services={catalog.services} value={presetId} onPick={p => setPresetId(p.id)} />
              <div style={{ fontSize: 12, color: 'var(--mut)', lineHeight: 1.5 }}>
                Prices, deliverables, schedule and terms come from the price list, before any combined credit. Everything stays editable in the builder.
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--mut)' }}>
              {catalog.loaded ? 'The price list could not be read, so there are no packages to offer — start blank and add services in the builder.' : 'Loading the price list…'}
            </div>
          )
        )}

        {kind !== 'bid' && (
          <fieldset style={{ margin: 0, padding: 0, border: 'none' }}>
            <legend style={{ fontSize: 13, fontWeight: 600, color: 'var(--txt)', marginBottom: 8, padding: 0 }}>Look</legend>
            <div role="radiogroup" aria-label="Proposal look" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
              {lookIds.map(id => {
                const L = LOOKS[id]; const on = look === id
                return (
                  <button key={id} type="button" role="radio" aria-checked={on} onClick={() => setLook(id)}
                    style={{ display: 'flex', gap: 10, alignItems: 'center', textAlign: 'left', padding: 10, cursor: 'pointer',
                      background: on ? 'var(--accent-soft)' : 'transparent', border: `1px solid ${on ? 'color-mix(in srgb, var(--accent) 45%, transparent)' : 'var(--bdr2)'}`, fontFamily: 'var(--fu)' }}>
                    <LookSwatch id={id} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: on ? 'var(--accent)' : 'var(--txt)' }}>{L.label}</span>
                      <span style={{ display: 'block', fontSize: 11.5, color: 'var(--mut)', lineHeight: 1.4, marginTop: 2 }}>{L.description}</span>
                    </span>
                  </button>
                )
              })}
            </div>
            <div style={{ fontSize: 12, color: 'var(--mut)', marginTop: 6 }}>You can change it later in the studio’s Settings.</div>
          </fieldset>
        )}

        {start === 'template' && (
          (templates.length || kind !== 'bid') ? (
            <select aria-label="Template" value={templateId} onChange={e => {
              const v = e.target.value
              setTemplateId(v)
              // A designed template is a look: pick it, and the copy generates.
              if (v.startsWith('look:')) setLook(v.slice(5))
            }} style={control}>
              <option value="">Choose a template…</option>
              {kind !== 'bid' && (
                <optgroup label="Designed templates">
                  {lookIds.map(id => <option key={id} value={`look:${id}`}>{LOOKS[id].label}, written for you from the client and price list</option>)}
                </optgroup>
              )}
              {templates.length > 0 && (
                <optgroup label="Saved templates">
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </optgroup>
              )}
            </select>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--mut)' }}>
              {data.loaded ? `No active ${kind === 'bid' ? 'bid' : 'proposal'} templates yet — build one under Templates, or start blank.` : 'Loading templates…'}
            </div>
          )
        )}

        {start === 'past' && (
          pasts.length ? (
            <select aria-label={kind === 'bid' ? 'Past bid' : 'Past proposal'} value={pastId} onChange={e => setPastId(e.target.value)} style={control}>
              <option value="">{kind === 'bid' ? 'Choose a past bid…' : 'Choose a past proposal…'}</option>
              {pasts.map(p => <option key={p.id} value={p.id}>{p.title || 'Untitled'}{p.organization ? ` · ${p.organization}` : ''}</option>)}
            </select>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--mut)' }}>{data.loaded ? `Nothing to copy from yet — start blank.` : 'Loading…'}</div>
          )
        )}

        {start === 'past' && kind === 'quote' && (
          <div style={{ fontSize: 12, color: 'var(--mut)', lineHeight: 1.5 }}>
            Its pages and lines are copied at the prices they were quoted at; each line shows the price-list version it came from.
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={onClose}
            style={{ height: 42, padding: '0 16px', border: '1px solid var(--bdr2)', borderRadius: 9, background: 'var(--card)', fontFamily: 'var(--fu)', fontSize: 14, fontWeight: 700, color: 'var(--navy)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="button" onClick={go} disabled={Boolean(blocker) || busy}
            style={{
              height: 42, padding: '0 18px', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', borderRadius: 9, fontFamily: 'var(--fu)', fontSize: 14, fontWeight: 700,
              background: blocker ? 'var(--surf)' : 'var(--accent-soft)', color: blocker ? 'var(--mut)' : 'var(--accent)',
              cursor: blocker || busy ? 'not-allowed' : 'pointer',
            }}>
            {busy ? 'Creating…' : blocker || (kind === 'bid' ? 'Open bid builder' : 'Open builder')}
          </button>
        </div>
      </div>
    </>,
    document.body,
  )
}
